import type { ConversationAssigneeGroupManagement } from 'src/graphql/entities/conversation-assignee-group';

export const ASSIGNEE_GROUP_NAME_MAX_LENGTH = 100;
export const ASSIGNEE_GROUP_DESCRIPTION_MAX_LENGTH = 500;

// any non-control printable characters, including
// letters, numbers, punctuation, whitespace, and unicode word characters.
// eslint-disable-next-line no-control-regex
export const ASSIGNEE_GROUP_NAME_ALLOWED_CHARS_REGEX = /^[^\x00-\x1F\x7F]+$/;

export const ASSIGNEE_GROUP_NAME_ERRORS = {
  Required: 'Name is required',
  TooLong: `Name must be ${ASSIGNEE_GROUP_NAME_MAX_LENGTH} characters or fewer`,
  InvalidChars: 'Name contains invalid characters',
  Duplicate: 'A group with this name already exists for this partner',
} as const;

export const ASSIGNEE_GROUP_DESCRIPTION_ERRORS = {
  TooLong: `Description must be ${ASSIGNEE_GROUP_DESCRIPTION_MAX_LENGTH} characters or fewer`,
} as const;

interface ValidateNameInput {
  name: string;
  partnerId: number | null;
  existingGroups: ConversationAssigneeGroupManagement[];
  editingId?: string;
}

export function validateAssigneeGroupName({
  name,
  partnerId,
  existingGroups,
  editingId,
}: ValidateNameInput): string | null {
  const trimmed = name.trim();

  if (!trimmed) return ASSIGNEE_GROUP_NAME_ERRORS.Required;
  if (trimmed.length > ASSIGNEE_GROUP_NAME_MAX_LENGTH) {
    return ASSIGNEE_GROUP_NAME_ERRORS.TooLong;
  }
  if (!ASSIGNEE_GROUP_NAME_ALLOWED_CHARS_REGEX.test(trimmed)) {
    return ASSIGNEE_GROUP_NAME_ERRORS.InvalidChars;
  }

  const partnerGroups = existingGroups.filter(
    (g) =>
      g.partnerId === partnerId && g.id !== editingId && !String(g.id).startsWith('temp-'),
  );
  const clash = partnerGroups.find(
    (g) => (g.name ?? '').trim().toLocaleLowerCase() === trimmed.toLocaleLowerCase(),
  );
  if (clash) return ASSIGNEE_GROUP_NAME_ERRORS.Duplicate;

  return null;
}
